
import React, { useState, useEffect } from 'react';
import { UserRole, Word, UserProgress, User } from './types';
import { MOCK_WORDS } from './constants';
import ReviewSession from './components/ReviewSession';
import AdminPanel from './components/AdminPanel';
import Auth from './components/Auth';

const App: React.FC = () => {
  const [currentUser, setCurrentUser] = useState<User | null>(() => {
    const saved = localStorage.getItem('vocab_current_user');
    return saved ? JSON.parse(saved) : null;
  });
  
  const [vocab, setVocab] = useState<Word[]>(() => {
    const saved = localStorage.getItem('vocab_data');
    return saved ? JSON.parse(saved) : MOCK_WORDS;
  });

  const [allUsers, setAllUsers] = useState<User[]>(() => {
    const saved = localStorage.getItem('vocab_users_db');
    // 默认提供一个 admin 账号，如果本地没存过的话
    const defaultUsers = [{ id: 'admin-id', username: 'admin', password: 'admin123', role: UserRole.ADMIN }];
    return saved ? JSON.parse(saved) : defaultUsers;
  });

  const [isReviewing, setIsReviewing] = useState(false);
  const [selectedUnit, setSelectedUnit] = useState<string>('Unit 1');
  const [reviewMode, setReviewMode] = useState<'all' | 'wrong'>('all');
  
  const [progress, setProgress] = useState<UserProgress[]>(() => {
    const saved = localStorage.getItem('vocab_progress');
    return saved ? JSON.parse(saved) : [];
  });

  useEffect(() => {
    localStorage.setItem('vocab_progress', JSON.stringify(progress));
  }, [progress]);

  useEffect(() => {
    localStorage.setItem('vocab_data', JSON.stringify(vocab));
  }, [vocab]);

  useEffect(() => {
    localStorage.setItem('vocab_users_db', JSON.stringify(allUsers));
  }, [allUsers]);

  useEffect(() => {
    if (currentUser) {
      localStorage.setItem('vocab_current_user', JSON.stringify(currentUser));
    } else {
      localStorage.removeItem('vocab_current_user');
    }
  }, [currentUser]);

  const units = Array.from(new Set(vocab.map(w => w.unit)));

  const handleUpdateProgress = (wordId: string, status: 'correct' | 'wrong' | 'forgot') => {
    if (!currentUser) return;

    setProgress(prev => {
      const existingIdx = prev.findIndex(p => p.wordId === wordId && p.userId === currentUser.id);
      const newProgress = [...prev];

      if (existingIdx >= 0) {
        const item = { ...newProgress[existingIdx] };
        if (status === 'correct') item.correctCount += 1;
        else item.wrongCount += 1;
        item.lastReviewStatus = status;
        newProgress[existingIdx] = item;
      } else {
        newProgress.push({
          userId: currentUser.id,
          username: currentUser.username,
          wordId,
          correctCount: status === 'correct' ? 1 : 0,
          wrongCount: (status === 'wrong' || status === 'forgot') ? 1 : 0,
          lastReviewStatus: status
        });
      }
      return newProgress;
    });
  };

  const handleAddUser = (user: User) => {
    setAllUsers(prev => [...prev, user]);
  };

  const handleDeleteUser = (userId: string) => {
    if (userId === 'admin-id') return;
    setAllUsers(prev => prev.filter(u => u.id !== userId));
  };

  const togglePriority = (wordId: string) => {
    setVocab(prev => prev.map(w => w.id === wordId ? { ...w, isPriority: !w.isPriority } : w));
  };

  const startReview = () => {
    setIsReviewing(true);
  };

  const getFilteredWords = () => {
    if (!currentUser) return [];
    let filtered = vocab.filter(w => w.unit === selectedUnit);
    if (reviewMode === 'wrong') {
      const wrongIds = progress
        .filter(p => p.wrongCount > 0 && p.userId === currentUser.id)
        .map(p => p.wordId);
      filtered = filtered.filter(w => wrongIds.includes(w.id));
      filtered.sort((a, b) => {
        const pA = progress.find(p => p.wordId === a.id && p.userId === currentUser.id)?.wrongCount || 0;
        const pB = progress.find(p => p.wordId === b.id && p.userId === currentUser.id)?.wrongCount || 0;
        return pB - pA;
      });
    }
    return filtered;
  };

  const handleLogout = () => {
    setCurrentUser(null);
    setIsReviewing(false);
  };

  if (!currentUser) {
    return <Auth allUsers={allUsers} onLogin={setCurrentUser} />;
  }

  if (isReviewing) {
    const words = getFilteredWords();
    if (words.length === 0) {
      return (
        <div className="flex flex-col items-center justify-center min-h-screen bg-slate-50">
          <div className="bg-white p-10 rounded-[2.5rem] shadow-xl text-center border border-slate-100">
            <div className="w-20 h-20 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-3xl mx-auto mb-6">
              <i className="fas fa-check"></i>
            </div>
            <h2 className="text-2xl font-bold mb-4 text-slate-800">当前没有需要复习的单词</h2>
            <button 
              onClick={() => setIsReviewing(false)} 
              className="bg-blue-600 text-white px-10 py-4 rounded-2xl font-bold shadow-lg hover:bg-blue-700 transition-all"
            >
              返回主页
            </button>
          </div>
        </div>
      );
    }
    return (
      <div className="min-h-screen bg-slate-50 pt-10">
        <button 
          onClick={() => setIsReviewing(false)}
          className="ml-8 mb-4 text-slate-500 hover:text-slate-800 font-bold transition-colors flex items-center gap-2"
        >
          <i className="fas fa-arrow-left"></i> 退出复习
        </button>
        <ReviewSession 
          words={words} 
          onUpdateProgress={handleUpdateProgress}
          onComplete={() => setIsReviewing(false)} 
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <nav className="bg-white/80 backdrop-blur-md border-b border-slate-100 px-8 py-4 flex justify-between items-center sticky top-0 z-50 shadow-sm">
        <div className="flex items-center gap-2">
          <div className="bg-blue-600 w-10 h-10 rounded-xl flex items-center justify-center text-white font-bold text-xl">
            V
          </div>
          <span className="text-xl font-bold tracking-tight">VocabMaster</span>
          {currentUser.role === UserRole.ADMIN && (
            <span className="bg-red-100 text-red-600 text-[10px] font-black px-2 py-0.5 rounded-full uppercase ml-2">ADMIN</span>
          )}
        </div>
        
        <div className="flex items-center gap-6">
          <div className="text-right hidden sm:block">
            <p className="text-xs text-slate-400 font-bold uppercase tracking-wider">当前用户</p>
            <p className="text-sm font-bold text-slate-700">{currentUser.username}</p>
          </div>
          <button 
            onClick={handleLogout}
            className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center text-slate-500 hover:bg-red-50 hover:text-red-500 transition-all"
          >
            <i className="fas fa-sign-out-alt"></i>
          </button>
        </div>
      </nav>

      {currentUser.role === UserRole.STUDENT ? (
        <main className="max-w-4xl mx-auto py-12 px-6">
          <div className="bg-gradient-to-br from-blue-600 to-indigo-700 rounded-[2.5rem] p-10 text-white mb-12 shadow-2xl shadow-blue-200 relative overflow-hidden">
            <h1 className="text-4xl font-extrabold mb-4">加油，{currentUser.username}！</h1>
            <p className="opacity-90 text-lg mb-8">利用 AI 语音识别技术通过“说”来巩固记忆。</p>
            <div className="flex gap-4">
              <div className="bg-white/10 backdrop-blur-md px-6 py-3 rounded-2xl">
                <p className="text-sm opacity-80">已掌握</p>
                <p className="text-2xl font-bold">{progress.filter(p => p.userId === currentUser.id && p.correctCount > 3).length}</p>
              </div>
              <div className="bg-white/10 backdrop-blur-md px-6 py-3 rounded-2xl">
                <p className="text-sm opacity-80">待复习 (错词)</p>
                <p className="text-2xl font-bold">{progress.filter(p => p.userId === currentUser.id && p.wrongCount > 0).length}</p>
              </div>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-white p-8 rounded-[2rem] shadow-sm border border-slate-100">
              <h3 className="text-xl font-bold mb-6 flex items-center gap-2">选择复习单元</h3>
              <div className="grid grid-cols-2 gap-3 mb-8">
                {units.map(unit => (
                  <button 
                    key={unit}
                    onClick={() => setSelectedUnit(unit)}
                    className={`py-3 rounded-xl font-medium transition-all border ${selectedUnit === unit ? 'bg-blue-50 border-blue-200 text-blue-600 shadow-sm' : 'border-slate-100 text-slate-500'}`}
                  >
                    {unit}
                  </button>
                ))}
              </div>

              <h3 className="text-xl font-bold mb-6">模式选择</h3>
              <div className="flex gap-4 mb-8">
                <button onClick={() => setReviewMode('all')} className={`flex-1 py-4 rounded-2xl font-bold transition-all border ${reviewMode === 'all' ? 'bg-blue-600 text-white' : 'bg-white text-slate-500'}`}>
                  全部复习
                </button>
                <button onClick={() => setReviewMode('wrong')} className={`flex-1 py-4 rounded-2xl font-bold transition-all border ${reviewMode === 'wrong' ? 'bg-red-500 text-white' : 'bg-white text-slate-500'}`}>
                  只背错词
                </button>
              </div>

              <button onClick={startReview} className="w-full py-5 bg-blue-600 text-white font-bold text-xl rounded-2xl shadow-xl shadow-blue-200">
                开始复习
              </button>
            </div>

            <div className="bg-white p-8 rounded-[2rem] shadow-sm border border-slate-100">
              <h3 className="text-xl font-bold mb-6">最近记录</h3>
              <div className="space-y-4">
                {progress.filter(p => p.userId === currentUser.id).slice(-5).reverse().map((p, i) => {
                  const word = vocab.find(w => w.id === p.wordId);
                  return (
                    <div key={i} className="flex justify-between items-center p-4 bg-slate-50 rounded-2xl">
                      <div><p className="font-bold">{word?.english}</p><p className="text-xs text-slate-400">{word?.pos}</p></div>
                      <span className={`text-xs px-3 py-1 rounded-full font-bold ${p.lastReviewStatus === 'correct' ? 'bg-green-100 text-green-600' : 'bg-red-100 text-red-600'}`}>
                        {p.lastReviewStatus === 'correct' ? '答对了' : '答错了'}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </main>
      ) : (
        <AdminPanel 
          words={vocab} 
          progress={progress} 
          users={allUsers}
          onAddUser={handleAddUser}
          onDeleteUser={handleDeleteUser}
          onTogglePriority={togglePriority}
        />
      )}
    </div>
  );
};

export default App;
